import { InfinityIcon } from "@xola/icons";
import { ComboBox, formatTime, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { getUnitsPrice } from "common";
import dayjs from "dayjs";
import { isNumber } from "lodash";
import React, { useMemo } from "react";
import { components, GroupBase, OptionProps, StylesConfig } from "react-select";
import { DisplayType, OPEN_UNLIMITED, PurchaseItemPrivacy, Timeslot } from "types";
import shallow from "zustand/shallow";
import { useSeller } from "../../hooks/useAccount";
import { getIsEventOverlap, getOpenCapacity } from "../../models/Timeslot";
import { useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { Currency } from "../Common/Currency";
import { NoTranslate } from "../Common/NoTranslate";
import { SecondaryButton } from "../Common/SecondaryButton";
import { usePurchaseResponseStore } from "../PurchaseCreate/store/usePurchaseResponseStore";
import { allowPastDates } from "@/stores/globalStore";

interface ArrivalTimePickerProps {
    value: number | null;
    timeslots: Timeslot[];
    maxButtons?: number;
    allowOverlap?: boolean;
    onChange: (value: number) => void;
    allowWaitlist?: boolean;
    initialArrival?: dayjs.Dayjs;
    minQuantity?: number;
}

export const ArrivalTimePicker = ({
    value,
    minQuantity: initialMinQuantity,
    timeslots = [],
    maxButtons = 8,
    initialArrival,
    allowOverlap = false,
    allowWaitlist = false,
    onChange,
}: ArrivalTimePickerProps) => {
    const slots = timeslots.filter((timeslot) => timeslot.time !== null);
    const quantity = useCreatePurchaseStore((state) => state.getActiveItem()?.quantity);

    if (slots.length === 0) {
        return null;
    }

    const minQuantity = initialMinQuantity ?? quantity ?? 0;

    return (
        <span>
            {slots.length <= maxButtons ? (
                <div className="grid grid-cols-3 gap-2 pt-4">
                    {slots.map((timeslot) => (
                        <TimeslotButton
                            key={timeslot.time}
                            value={value}
                            timeslot={timeslot}
                            allowOverlap={allowOverlap}
                            allowWaitlist={allowWaitlist}
                            initialArrival={initialArrival}
                            isDisabled={minQuantity > timeslot.capacity.open && !allowWaitlist}
                            onClick={onChange}
                        />
                    ))}
                </div>
            ) : (
                <TimeslotDropdown
                    value={value}
                    timeslots={slots}
                    allowOverlap={allowOverlap}
                    minQuantity={minQuantity}
                    allowWaitlist={allowWaitlist}
                    initialArrival={initialArrival}
                    onChange={onChange}
                />
            )}
        </span>
    );
};

interface TimeslotDropdownProps {
    allowWaitlist: boolean;
    allowOverlap: boolean;
    timeslots: Timeslot[];
    value: number | null;
    minQuantity: number;
    onChange: (value: number) => void;
    initialArrival?: dayjs.Dayjs;
}

interface OptionWithWaitListProps {
    readonly value: string;
    readonly label: string;
    readonly timeslot: Timeslot;
    readonly isDisabled?: boolean;
    readonly canWaitList?: boolean;
}

const TimeslotDropdown = ({
    value,
    timeslots,
    allowOverlap,
    allowWaitlist,
    minQuantity,
    initialArrival,
    onChange,
}: TimeslotDropdownProps) => {
    const canBookPastDate = allowPastDates();
    const quantity = useCreatePurchaseStore((state) => state.getActiveItem()?.quantity);
    const handleOnChange = (option: any) => {
        onChange(Number(option.value));
    };

    const options = timeslots.map((timeslot) => {
        const canBook = timeslot.canBook || (timeslot.capacity.open > 0 && canBookPastDate);
        const isInitialTimeslot = timeslot.arrivalDatetime.isSame(initialArrival);
        const isEventOverlap = initialArrival ? getIsEventOverlap(timeslot, initialArrival) && allowOverlap : false;

        const canOnlyJoinWaitlist = timeslot.canBook && quantity > timeslot.capacity.open;
        const isCapacityExceeds = minQuantity > timeslot.capacity.open && !allowWaitlist; // check is open count is enough to accommodate reserved count
        const isDisabled = allowOverlap
            ? false
            : isCapacityExceeds ||
              (!canBook && !(allowWaitlist && timeslot.canWaitlist) && !isInitialTimeslot && !isEventOverlap);

        return {
            value: timeslot.time,
            label: formatTime(timeslot.time),
            timeslot,
            isDisabled,
            canWaitList: (timeslot.canWaitlist || canOnlyJoinWaitlist) && allowWaitlist,
        };
    });

    const optionStyles: StylesConfig<OptionWithWaitListProps> = {
        menuPortal: (base: any) => ({ ...base, zIndex: 30 }),
        option: ({ backgroundColor, display, color, fontSize, padding, ...rest }) => rest,
    };

    return (
        <ComboBox
            isSearchable={false}
            options={options}
            value={isNumber(value) ? { value, label: formatTime(value) } : { value: null, label: "Pick a time" }}
            placeholder="Select a timeslot"
            className="my-4 w-80"
            styles={optionStyles}
            components={{ Option: OptionWithWaitList }}
            menuPortalTarget={document.body}
            onChange={handleOnChange}
        />
    );
};

const OptionWithWaitList = (
    props: JSX.IntrinsicAttributes & OptionProps<OptionWithWaitListProps, boolean, GroupBase<OptionWithWaitListProps>>,
) => {
    const { data, isFocused } = props;

    const classNames = useMemo(() => {
        let classes;
        if (data?.isDisabled) {
            classes = ["!bg-gray-lighter", "text-gray-dark", "hover:!bg-gray-lighter"];
        } else if (props.isSelected) {
            classes = ["!bg-primary", "font-bold", "text-white"];
        } else if (data?.canWaitList) {
            classes = ["py-3.25", "!bg-yellow-lighter", "text-yellow-darker", "hover:!bg-yellow-lightish"];
        } else {
            classes = ["hover:!bg-blue-lighter"];
        }

        if (isFocused) {
            classes.push("!bg-white");
        }

        return classes;
    }, [data, props.isSelected, isFocused]);

    const OptionDisplayElement = () => {
        if (data?.canWaitList) {
            return <span>Waitlist</span>;
        }

        return <TimeslotInfo timeslot={data.timeslot} />;
    };

    const { unavailability } = data.timeslot;
    const optionContent = (
        <>
            <span>{data.label}</span>
            {OptionDisplayElement && (
                <span
                    className={clsx(
                        "text-sm",
                        data?.canWaitList && "bg-yellow-lightish font-normal text-yellow-darker",
                    )}
                    style={{ borderRadius: 80, padding: "3px 8px" }}
                >
                    <OptionDisplayElement />
                </span>
            )}
        </>
    );

    return (
        <components.Option
            {...props}
            className={clsx("flex justify-between whitespace-nowrap border-t border-gray-light py-4 px-3", classNames)}
        >
            {unavailability?.message ? (
                <Tooltip
                    content={<NoTranslate className="whitespace-pre-line">{unavailability.message}</NoTranslate>}
                    placement="right"
                    className="flex w-full justify-between"
                >
                    {optionContent}
                </Tooltip>
            ) : (
                optionContent
            )}
        </components.Option>
    );
};

interface TimeslotButtonProps {
    timeslot: Timeslot;
    value: number | null;
    onClick: (time: number) => void;
    allowWaitlist: boolean;
    allowOverlap: boolean;
    initialArrival?: dayjs.Dayjs;
    isDisabled?: boolean;
}

const TimeslotButton = ({
    value,
    timeslot,
    allowOverlap,
    allowWaitlist,
    initialArrival,
    isDisabled = false,
    onClick,
}: TimeslotButtonProps) => {
    const seller = useSeller();
    const [itemProductId, itemId, quantity] = useCreatePurchaseStore(
        (state) => [state.getActiveItem()?.product?.id, state.getActiveItem()?.id, state.getActiveItem()?.quantity],
        shallow,
    );

    const isEventOverlap = initialArrival ? getIsEventOverlap(timeslot, initialArrival) && allowOverlap : null;

    const isActive = timeslot.time === value;
    const canOnlyJoinWaitlist = allowWaitlist && timeslot.canBook && quantity > timeslot.capacity.open;
    const isWaitlisted = timeslot.canWaitlist || canOnlyJoinWaitlist;

    const { showUnavailableTimes } = seller.preferences.datepickerPreference;

    const canBook = timeslot.canBook || (timeslot.capacity.open > 0 && allowPastDates());
    const isInitialTimeslot = timeslot.arrivalDatetime.isSame(initialArrival);
    const isDisabledLocal = isDisabled || (!canBook && !(allowWaitlist && isWaitlisted) && !isInitialTimeslot); // FIXME: Confusing please document

    const isMist = useCreatePurchaseStore((state) => state.isMist());
    const isTimeslotDisabledForMist = isMist ? isSlotDisabledForMist(timeslot, itemProductId, itemId) : false;

    const handleClick = () => {
        if (timeslot.time) {
            onClick(timeslot.time);
        }
    };

    if (isDisabledLocal && !showUnavailableTimes && !isEventOverlap && !allowOverlap) {
        return null;
    }

    return (
        <SecondaryButton
            size="medium"
            disabled={((isDisabledLocal && !isEventOverlap) || isTimeslotDisabledForMist) && !allowOverlap}
            className={clsx(
                "flex items-center justify-center whitespace-nowrap py-4 px-0",
                isActive && "border border-blue-light bg-blue-lighter hover:bg-blue-lighter",
                !isWaitlisted && !canBook && !isEventOverlap && "bg-gray-light",
                isWaitlisted && "border border-yellow hover:border-yellow hover:bg-yellow-lighter",
                isWaitlisted && "active:bg-yellow-lightish disabled:bg-yellow-lighter disabled:text-yellow",
                {
                    "text-yellow-dark": isWaitlisted && !isActive,
                    "bg-yellow-lightish text-yellow-darker": isWaitlisted && isActive,
                },
            )}
            onClick={handleClick}
        >
            <Tooltip
                content={
                    isTimeslotDisabledForMist ? (
                        "Only one reservation can be made at a time for the exact same event. (You already have a reservation in your cart for this exact event)"
                    ) : (
                        <NoTranslate className="whitespace-pre-line">{timeslot.unavailability?.message}</NoTranslate>
                    )
                }
                disabled={!isTimeslotDisabledForMist && !timeslot.unavailability?.message}
                className="w-full"
            >
                <div className="m-1 space-y-0.5">
                    <NoTranslate as="div" className="font-normal">
                        {formatTime(timeslot.time)}
                    </NoTranslate>
                    <div>
                        <TimeslotInfo timeslot={timeslot} />
                    </div>
                </div>
            </Tooltip>
        </SecondaryButton>
    );
};

export const TimeslotInfo = ({ timeslot }: { timeslot: Timeslot }) => {
    const seller = useSeller();
    // getOpenCapacity reports equipment unit counts for equipment slots
    const open = getOpenCapacity(timeslot);
    const { displayType, priceDisplay } = seller.preferences.datepickerPreference;

    const price = getUnitsPrice(priceDisplay, timeslot.units)?.price;
    if (price && displayType === DisplayType.PRICE) {
        return <Currency shouldRemoveTrailingZeroes={false}>{price}</Currency>;
    }

    if (displayType === DisplayType.COUNT) {
        return (
            <span>
                <var data-var="openCount">{open >= OPEN_UNLIMITED ? <InfinityIcon /> : open}</var>&nbsp;open
            </span>
        );
    }

    return null;
};

const isSlotDisabledForMist = (timeslot: Timeslot, itemProductId?: ID, itemId?: ID) => {
    const purchaseItems = usePurchaseResponseStore.getState().purchase.items;
    if (!purchaseItems) {
        return false;
    }

    let isDisabled = false;
    for (const item of purchaseItems) {
        if (isDisabled) {
            return isDisabled;
        }

        const isSameProduct = item.product.id === itemProductId;
        if (isSameProduct && "arrivalDateTime" in item && item.id !== itemId) {
            const isSameSlot = timeslot.arrivalDatetime.isSame(item.arrivalDateTime);
            const isTheSameSlotUsedForPrivateBooking = item.privacy === PurchaseItemPrivacy.PRIVATE && isSameSlot;
            isDisabled = Boolean(isTheSameSlotUsedForPrivateBooking || (isSameSlot && timeslot.capacity.open <= 1));
        }
    }

    return isDisabled;
};
